// GD-73
import { OPTIMIZELY_EVENTS } from 'src/constants'

const options = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
}

function handleGeolocationSuccess(pos: GeolocationPosition) {
  const crd = pos.coords

  return crd
}

interface ErrorProps {
  geolocationPositionError: GeolocationPositionError
  sendOptimizelyEvent: HandleNearbyPropertiesClickProps['sendOptimizelyEvent']
  warningLogger: HandleNearbyPropertiesClickProps['warningLogger']
}

function handleGeolocationErrors({
  geolocationPositionError,
  sendOptimizelyEvent,
  warningLogger,
}: ErrorProps) {
  switch (geolocationPositionError.code) {
    case geolocationPositionError.PERMISSION_DENIED:
      warningLogger('GD-73: User denied the request for Geolocation.')

      sendOptimizelyEvent(
        OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_GEOLOCATION_ACCESS_DENIED,
      )
      break
    case geolocationPositionError.POSITION_UNAVAILABLE:
      warningLogger('GD-73: Location information is unavailable.')
      break
    case geolocationPositionError.TIMEOUT:
      warningLogger('GD-73: The request to get user location timed out.')
      break
    default:
      warningLogger(
        `GD-73: ERROR(${geolocationPositionError.code}): ${geolocationPositionError.message}`,
      )
  }

  if (
    geolocationPositionError.code !== geolocationPositionError.PERMISSION_DENIED
  ) {
    sendOptimizelyEvent(OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_GEOLOCATION_ERROR)
  }

  return geolocationPositionError
}

interface HandleNearbyPropertiesClickProps {
  sendOptimizelyEvent: (eventKey: string) => void
  warningLogger: (message: string) => void
}

type HandleNearbyPropertiesClickResponse = GeolocationCoordinates

export const handleNearbyPropertiesClick = async ({
  sendOptimizelyEvent,
  warningLogger,
}: HandleNearbyPropertiesClickProps): Promise<HandleNearbyPropertiesClickResponse> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Needs to be run in a browser'))
  }

  if (!window.navigator?.geolocation) {
    warningLogger('GD-73: Geolocation is not supported by this browser.')

    return Promise.reject(
      new Error('Geolocation is not supported by this browser.'),
    )
  }

  const coords = new Promise<HandleNearbyPropertiesClickResponse>(
    (resolve, reject) => {
      window.navigator.geolocation.getCurrentPosition(
        (pos) => {
          const crd = handleGeolocationSuccess(pos)

          return resolve(crd)
        },
        (err) => {
          const errorObject = handleGeolocationErrors({
            geolocationPositionError: err,
            sendOptimizelyEvent,
            warningLogger,
          })

          return reject(new Error(errorObject.message, { cause: errorObject }))
        },
        options,
      )
    },
  )

  return await coords
}
